import React, { useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WaitlistDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ name: "", phone: "", comment: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Заповни ім'я та номер телефону");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        comment: form.comment.trim(),
      });
      setDone(true);
    } catch (err) {
      console.error(err);
      toast.error("Помилка. Спробуй ще раз або напиши нам в Instagram.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (v) => {
    onOpenChange(v);
    if (!v) {
      setTimeout(() => {
        setDone(false);
        setForm({ name: "", phone: "", comment: "" });
      }, 300);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="waitlist-dialog"
        className="bg-[#0F0F12] border border-white/10 text-[#FDFBF7] max-w-md sm:max-w-lg p-0 overflow-hidden"
      >
        <div className="p-6 sm:p-8">
          {done ? (
            <div className="text-center py-6" data-testid="waitlist-success">
              <span className="mx-auto mb-6 flex w-14 h-14 items-center justify-center rounded-full border border-[#A01828]/50 bg-[#7A1220]/15">
                <Check className="w-6 h-6 text-[#A01828]" />
              </span>
              <h3 className="font-serif-display text-3xl sm:text-4xl font-light leading-tight">
                Ти у списку
                <br />
                <span className="italic text-[#A01828]">очікування</span>
              </h3>
              <p className="text-white/60 text-sm mt-4 leading-relaxed max-w-xs mx-auto">
                Щойно відкриється набір у березневі групи Анастасії — ми напишемо тобі першій.
              </p>
              <button
                onClick={() => handleOpenChange(false)}
                data-testid="waitlist-done-button"
                className="mt-8 inline-flex items-center justify-center border border-white/15 hover:border-[#A01828] transition-colors px-10 h-12 text-xs uppercase tracking-[0.2em]"
              >
                Готово
              </button>
            </div>
          ) : (
            <>
              <DialogHeader className="text-left space-y-2 mb-6">
                <p className="overline">Лист очікування · березень</p>
                <DialogTitle className="font-serif-display text-3xl sm:text-4xl font-light leading-tight">
                  Місце у групі
                  <br />
                  <span className="italic text-[#A01828]">Анастасії</span>
                </DialogTitle>
                <DialogDescription className="text-[#A19E98] text-sm">
                  Групи заповнені. Залиш ім'я та номер телефону — повідомимо про відкриття
                  березневого набору першій.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="wl-name" className="text-xs uppercase tracking-widest text-[#A19E98]">
                    Ім'я
                  </Label>
                  <Input
                    id="wl-name"
                    data-testid="waitlist-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Анна"
                    className="mt-2 bg-[#141417] border-white/10 text-[#FDFBF7] placeholder:text-white/30 focus:border-[#7A1220] focus-visible:ring-0 rounded-none h-12"
                  />
                </div>

                <div>
                  <Label htmlFor="wl-phone" className="text-xs uppercase tracking-widest text-[#A19E98]">
                    Номер телефону
                  </Label>
                  <Input
                    id="wl-phone"
                    type="tel"
                    data-testid="waitlist-phone-input"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+48 ..."
                    className="mt-2 bg-[#141417] border-white/10 text-[#FDFBF7] placeholder:text-white/30 focus:border-[#7A1220] focus-visible:ring-0 rounded-none h-12"
                  />
                </div>

                <div>
                  <Label htmlFor="wl-comment" className="text-xs uppercase tracking-widest text-[#A19E98]">
                    Коментар <span className="text-white/30 normal-case tracking-normal">(не обов'язково)</span>
                  </Label>
                  <Textarea
                    id="wl-comment"
                    data-testid="waitlist-comment-input"
                    rows={2}
                    value={form.comment}
                    onChange={(e) => setForm({ ...form, comment: e.target.value })}
                    placeholder="Наприклад: цікавить вівторок ввечері"
                    className="mt-2 bg-[#141417] border-white/10 text-[#FDFBF7] placeholder:text-white/30 focus:border-[#7A1220] focus-visible:ring-0 rounded-none resize-none"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="waitlist-submit-button"
                  disabled={loading}
                  className="w-full h-14 bg-[#7A1220] hover:bg-[#A01828] active:scale-[0.99] transition-all duration-300 text-[#FDFBF7] font-medium tracking-wider uppercase text-xs disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Зберігаємо...
                    </>
                  ) : (
                    <>Долучитись до листа очікування</>
                  )}
                </button>

                <p className="text-[11px] text-white/40 leading-relaxed text-center pt-1">
                  Ми напишемо тобі особисто, щойно відкриється набір. Жодного спаму.
                </p>
              </form>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
